import type { EmojiClickEvent } from "emoji-picker-element/shared";
import { ApiError, dmApi } from "../api";
import { presetFor } from "../avatar/catalog";
import { avatarPartsReady, headDataURL } from "../avatar/composer";
import { escapeHtml } from "../moderation/format";
import type { Session } from "../session";
import {
  DM_GROUP_MAX,
  DM_GROUP_NAME_MAX,
  DM_MAX_CHARS,
  type DMConversation,
  type DMMessage,
  type DMUser,
} from "../types";
import { confirmAction } from "./ConfirmDialog";
import { loadEmojiPicker } from "./emojiPicker";
import { isolateKeyboardFromGame } from "./keyboard";

/** Escolher pessoas: conversa nova a dois, grupo novo ou adicionar ao grupo aberto. */
type PickMode = "direct" | "group" | "add";

/** O que abre ao lado da coluna de conversas. */
type Pane = { kind: "conversation" } | { kind: "members" } | { kind: "pick"; mode: PickMode };

const SEARCH_DELAY = 250;
const EXPANDED_KEY = "foxygarden.dm.expanded";

/** Larguras (px) da coluna contraida, da coluna larga e do painel ao lado. Iguais as do style.css. */
const RAIL_WIDTH = 64;
const LIST_WIDTH = 300;
const PANE_WIDTH = 340;

/**
 * Mensagens privadas numa barra na lateral esquerda. A coluna de conversas
 * contrai para so as miniaturas (cabeca do avatar, ou mosaico dos membros no
 * grupo) ou expande com nome e ultima mensagem. A conversa aberta, a escolha
 * de pessoas e os membros do grupo abrem num painel ao lado dela, com a
 * coluna contraida. O HUD anda para a direita para dar lugar (--dm-width).
 *
 * No toque nao ha barra fixa: o envelope do topo abre tudo em tela cheia,
 * a lista ou o painel. As mensagens novas chegam pelo WebSocket (receive).
 */
export class DmPanel {
  private readonly root = document.createElement("aside");
  private readonly column = document.createElement("div");
  private readonly paneEl = document.createElement("section");
  /** Coluna larga (sem painel aberto). Lembrado entre as visitas. */
  private expanded = loadExpanded();
  private pane: Pane | null = null;
  /** No toque: a tela cheia esta aberta. */
  private touchOpen = false;
  /** Escondida durante os minigames. */
  private suspended = false;
  private conversations: DMConversation[] = [];
  private listLoaded = false;
  private listError = "";
  /** A conversa aberta no painel (ou de onde vieram membros/adicionar). */
  private current: DMConversation | null = null;
  private messages: DMMessage[] = [];
  private more = false;
  private log: HTMLOListElement | null = null;
  /** Pessoas escolhidas (grupo novo e adicionar). */
  private picked = new Map<string, string>();
  private searchTimer: number | null = null;
  private readTimer: number | null = null;
  private loadSeq = 0;
  private destroyed = false;
  /** O seletor de emojis esta aberto sobre o campo da conversa. */
  private emojiOpen = false;
  /**
   * O seletor (um so para a pagina, ver emojiPicker.ts) enquanto esta neste
   * painel: ao fechar, o ouvinte sai dele, senao o seletor seguraria este
   * painel mesmo depois de destruido.
   */
  private picker: HTMLElement | null = null;

  private unreadHandler?: (count: number) => void;
  private openHandler?: (open: boolean) => void;
  private dossierHandler?: (userId: string) => void;

  constructor(private readonly session: Session) {
    this.root.id = "dm";
    this.root.setAttribute("aria-label", "Mensagens privadas");
    this.column.className = "dm-col";
    this.paneEl.className = "dm-pane";
    this.root.append(this.column, this.paneEl);
    document.body.append(this.root);
    isolateKeyboardFromGame(this.root);

    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      this.escape();
    });
    this.root.addEventListener("click", (event) => this.onClick(event));
    document.addEventListener("visibilitychange", this.onVisibility);
    window.visualViewport?.addEventListener("resize", this.fitViewport);
    window.visualViewport?.addEventListener("scroll", this.fitViewport);

    this.renderColumn();
    this.layout();
    void this.reloadList();
  }

  private get me(): string {
    return this.session.user.id;
  }

  private get token(): string {
    return this.session.token;
  }

  private get touch(): boolean {
    return document.body.classList.contains("touch");
  }

  /** Total de nao lidas mudou (selo da barra e do envelope no HUD). */
  onUnreadChange(handler: (count: number) => void): void {
    this.unreadHandler = handler;
  }

  /** No toque: a tela cheia abriu ou fechou (a cena esconde os controles). */
  onOpenChange(handler: (open: boolean) => void): void {
    this.openHandler = handler;
  }

  /** Clique num nome ou cabecalho: abrir o dossie da pessoa. */
  onViewDossier(handler: (userId: string) => void): void {
    this.dossierHandler = handler;
  }

  /** Abre a conversa a dois com a conta ("Mandar mensagem" do dossie). */
  async openDirectWith(userId: string): Promise<void> {
    if (this.destroyed) return;
    if (this.touch) this.openTouch();
    try {
      const { conversation } = await dmApi.direct(this.token, userId);
      this.upsert(conversation);
      await this.openConversation(conversation);
    } catch (error) {
      this.setPane({ kind: "pick", mode: "direct" });
      this.flash(errorText(error));
    }
  }

  /** Envelope do HUD (so aparece no toque): abre ou fecha a tela cheia. */
  toggle(): void {
    if (this.touchOpen) this.closeTouch();
    else this.openTouch();
  }

  private openTouch(): void {
    if (this.touchOpen || this.destroyed) return;
    this.touchOpen = true;
    this.layout();
    this.fitViewport();
    this.openHandler?.(true);
    if (this.pane?.kind === "conversation") this.scheduleRead();
    else void this.reloadList();
  }

  private closeTouch(): void {
    if (!this.touchOpen) return;
    this.touchOpen = false;
    this.blurInside();
    this.layout();
    this.openHandler?.(false);
  }

  /** Some durante os minigames (a barra ficaria por cima do jogo). */
  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
    if (suspended) {
      this.blurInside();
      this.closeTouch();
    }
    this.layout();
  }

  /** Reconectou ao mundo: o que chegou com a conexao caida so vem do servidor. */
  resync(): void {
    void this.reloadList();
    if (this.current && this.pane?.kind === "conversation") void this.loadMessages(this.current, true);
  }

  destroy(): void {
    this.destroyed = true;
    this.closeEmoji();
    this.closeTouch();
    if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
    if (this.readTimer !== null) window.clearTimeout(this.readTimer);
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.visualViewport?.removeEventListener("resize", this.fitViewport);
    window.visualViewport?.removeEventListener("scroll", this.fitViewport);
    document.documentElement.style.removeProperty("--dm-width");
    this.root.remove();
  }

  // ------------------------------------------------------- tempo real

  /** Mensagem nova vinda do WebSocket (tambem as que esta aba mandou). */
  receive(msg: DMMessage): void {
    const conv = this.conversations.find((c) => c.id === msg.conversationId);
    const mine = msg.senderId === this.me;
    const viewing = this.isViewing(msg.conversationId);

    // conversa desconhecida (alguem comecou ou te pos num grupo) ou aviso do
    // grupo (nome e membros mudaram): a lista vem de novo do servidor
    if (!conv || msg.kind === "system") {
      void this.reloadList();
    } else {
      conv.last = msg;
      conv.lastMessageAt = msg.at;
      if (!mine && !viewing) conv.unread++;
      this.sortList();
    }
    if (this.current?.id === msg.conversationId && this.log) {
      this.appendMessage(msg);
      if (viewing && !mine) this.scheduleRead();
    }
    this.renderColumn();
    this.emitUnread();
  }

  /** A conversa esta na tela e a pessoa pode estar lendo. */
  private isViewing(id: string): boolean {
    const shown = this.touch ? this.touchOpen : !this.suspended;
    return shown && this.pane?.kind === "conversation" && this.current?.id === id && document.visibilityState === "visible";
  }

  private readonly onVisibility = () => {
    if (this.current && this.isViewing(this.current.id)) this.scheduleRead();
  };

  // ------------------------------------------------------------ dados

  private async reloadList(): Promise<void> {
    try {
      const { conversations } = await dmApi.conversations(this.token);
      if (this.destroyed) return;
      this.conversations = conversations;
      this.listLoaded = true;
      this.listError = "";
      if (this.current) {
        const fresh = conversations.find((c) => c.id === this.current?.id);
        if (fresh) {
          // aberta na tela: o que chegou ja foi visto (a marcacao pode estar a caminho)
          if (this.isViewing(fresh.id)) fresh.unread = 0;
          this.current = fresh;
          this.updateConversationHeader();
        } else if (this.current.kind === "group") {
          // saiu do grupo (em outra aba)
          this.current = null;
          if (this.pane && this.pane.kind !== "pick") this.setPane(null);
        }
      }
    } catch (error) {
      this.listError = errorText(error);
    }
    this.renderColumn();
    this.emitUnread();
  }

  private upsert(conv: DMConversation): void {
    const i = this.conversations.findIndex((c) => c.id === conv.id);
    if (i >= 0) this.conversations[i] = conv;
    else this.conversations.unshift(conv);
    this.sortList();
  }

  private sortList(): void {
    this.conversations.sort((a, b) => Date.parse(b.lastMessageAt) - Date.parse(a.lastMessageAt));
  }

  private emitUnread(): void {
    const total = this.conversations.reduce((sum, c) => sum + c.unread, 0);
    const badge = this.column.querySelector<HTMLSpanElement>("[data-total]");
    if (badge) {
      badge.textContent = total > 99 ? "99+" : String(total);
      badge.classList.toggle("hidden", total <= 0);
    }
    this.unreadHandler?.(total);
  }

  private async openConversation(conv: DMConversation): Promise<void> {
    this.current = conv;
    this.messages = [];
    this.more = false;
    this.setPane({ kind: "conversation" });
    await this.loadMessages(conv, true);
  }

  /** Carrega as ultimas mensagens (fresh) ou as anteriores as da tela. */
  private async loadMessages(conv: DMConversation, fresh: boolean): Promise<void> {
    const seq = ++this.loadSeq;
    const before = fresh ? 0 : (this.messages[0]?.id ?? 0);
    try {
      const page = await dmApi.messages(this.token, conv.id, before);
      if (seq !== this.loadSeq || this.current?.id !== conv.id || this.destroyed) return;
      if (fresh) {
        this.messages = page.messages;
      } else {
        const known = new Set(this.messages.map((m) => m.id));
        this.messages = [...page.messages.filter((m) => !known.has(m.id)), ...this.messages];
      }
      this.more = page.more;
      this.renderLog(fresh);
      if (fresh) this.scheduleRead();
    } catch (error) {
      if (seq === this.loadSeq) this.flash(errorText(error));
    }
  }

  /** Marca como lido ate a ultima mensagem da tela (agrupa rajadas). */
  private scheduleRead(): void {
    if (this.readTimer !== null) window.clearTimeout(this.readTimer);
    this.readTimer = window.setTimeout(() => {
      this.readTimer = null;
      const conv = this.current;
      const last = this.messages.at(-1);
      if (!conv || !last || !this.isViewing(conv.id)) return;
      const listed = this.conversations.find((c) => c.id === conv.id);
      if (listed && listed.unread) {
        listed.unread = 0;
        this.renderColumn();
      }
      if (listed) listed.lastReadId = Math.max(listed.lastReadId, last.id);
      this.emitUnread();
      void dmApi.markRead(this.token, conv.id, last.id).catch(() => {
        // fica como nao lida; a proxima abertura tenta de novo
      });
    }, 300);
  }

  // ---------------------------------------------------------- layout

  /**
   * Aplica o estado na barra: coluna larga so sem painel, a largura que o HUD
   * precisa deixar livre (--dm-width, ignorada no toque) e a tela cheia do toque.
   */
  private layout(): void {
    const wide = this.expanded && !this.pane;
    this.root.classList.toggle("wide", wide);
    this.root.classList.toggle("with-pane", !!this.pane);
    this.root.classList.toggle("touch-open", this.touchOpen);
    this.root.classList.toggle("hidden", this.suspended);
    const width = this.suspended ? 0 : (wide ? LIST_WIDTH : RAIL_WIDTH) + (this.pane ? PANE_WIDTH : 0);
    document.documentElement.style.setProperty("--dm-width", `${width}px`);
  }

  private setPane(pane: Pane | null): void {
    this.closeEmoji();
    this.blurInside();
    this.pane = pane;
    this.log = null;
    if (pane?.kind !== "pick") this.picked.clear();
    this.renderPane();
    this.renderColumn();
    this.layout();
  }

  private setExpanded(expanded: boolean): void {
    this.expanded = expanded;
    saveExpanded(expanded);
    this.renderColumn();
    this.layout();
  }

  /** Esc: volta um passo (adicionar/membros -> conversa -> fecha o painel -> contrai). */
  private escape(): void {
    if (this.emojiOpen) {
      this.closeEmoji();
    } else if (this.pane && (this.pane.kind === "members" || (this.pane.kind === "pick" && this.pane.mode === "add"))) {
      this.setPane({ kind: "conversation" });
    } else if (this.pane) {
      this.closePane();
    } else if (this.touch) {
      this.closeTouch();
    } else if (this.expanded) {
      this.setExpanded(false);
    }
  }

  private closePane(): void {
    this.current = null;
    this.setPane(null);
  }

  private blurInside(): void {
    // o foco num campo escondido seguraria o teclado longe do jogo
    if (this.root.contains(document.activeElement)) (document.activeElement as HTMLElement).blur();
  }

  // ---------------------------------------------------------- coluna

  private renderColumn(): void {
    const wide = this.expanded && !this.pane;
    const items = this.conversations
      .map((c) => {
        const title = this.titleOf(c);
        const preview = c.last ? this.previewOf(c, c.last) : c.kind === "group" ? "Grupo criado" : "Sem mensagens ainda";
        const active = this.pane && this.current?.id === c.id;
        return `
        <li><button type="button" class="dm-item${c.unread ? " unread" : ""}${active ? " active" : ""}" data-open="${escapeHtml(c.id)}" title="${escapeHtml(title)}">
          <span class="dm-thumb">${this.convAvatar(c)}${c.unread ? `<span class="dm-count">${c.unread > 99 ? "99+" : c.unread}</span>` : ""}</span>
          <span class="dm-item-main">
            <span class="dm-item-top"><strong>${escapeHtml(title)}</strong><time>${c.last ? shortTime(c.last.at) : ""}</time></span>
            <span class="dm-item-preview">${escapeHtml(preview)}</span>
          </span>
        </button></li>`;
      })
      .join("");

    const empty = !this.listLoaded
      ? this.listError || "Carregando…"
      : "Nenhuma conversa ainda. Use o + para falar com alguém ou montar um grupo.";
    const toggleLabel = wide ? "Contrair a barra" : "Expandir a barra";
    this.column.innerHTML = `
      <header class="dm-col-head">
        <button type="button" class="dm-icon dm-toggle" data-act="toggle" title="${toggleLabel}" aria-label="${toggleLabel}" aria-expanded="${wide}">
          ${
            wide
              ? `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m11 6-6 6 6 6M19 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>`
              : `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="2" /><path d="m4 7 8 6 8-6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
          <span class="hud-badge hidden" data-total></span>`
          }
        </button>
        <strong class="dm-col-title">Mensagens</strong>
        <button type="button" class="dm-icon dm-plus" data-act="new" title="Nova conversa ou grupo" aria-label="Nova conversa ou grupo">+</button>
        <button type="button" class="dm-icon dm-touch-close" data-act="close-touch" title="Fechar" aria-label="Fechar">✕</button>
      </header>
      <ul class="dm-list dm-convs">${items || `<li class="dm-empty">${escapeHtml(empty)}</li>`}</ul>
    `;
    this.emitUnread();
  }

  // ---------------------------------------------------------- painel

  private renderPane(): void {
    const pane = this.pane;
    if (!pane) {
      this.paneEl.replaceChildren();
      return;
    }
    switch (pane.kind) {
      case "conversation":
        return this.renderConversation();
      case "members":
        return this.renderMembers();
      case "pick":
        return this.renderPick(pane.mode);
    }
  }

  private renderConversation(): void {
    const conv = this.current;
    if (!conv) return this.setPane(null);
    this.paneEl.innerHTML = `
      <header class="dm-head">
        <span data-head-ava></span>
        <div class="dm-title" data-title></div>
        ${conv.kind === "group" ? `<button type="button" class="dm-icon" data-act="members" title="Membros e opções" aria-label="Membros e opções">⋯</button>` : ""}
        <button type="button" class="dm-icon" data-act="close-pane" title="Fechar conversa (Esc)" aria-label="Fechar conversa">✕</button>
      </header>
      <p class="notice error hidden" data-notice></p>
      <ol class="dm-log" aria-live="polite"></ol>
      <div class="dm-emoji hidden" data-emoji></div>
      <form class="dm-form">
        <button type="button" class="dm-emoji-btn" data-act="emoji" title="Emojis" aria-label="Emojis" aria-expanded="false">😊</button>
        <input type="text" maxlength="${DM_MAX_CHARS}" autocomplete="off" autocapitalize="sentences" enterkeyhint="send" aria-label="Mensagem" />
        <button type="submit" class="dm-send" aria-label="Enviar">Enviar</button>
      </form>
    `;
    this.log = this.paneEl.querySelector<HTMLOListElement>(".dm-log");
    this.updateConversationHeader();
    this.renderLog(true);

    const form = this.paneEl.querySelector<HTMLFormElement>(".dm-form")!;
    const input = form.querySelector<HTMLInputElement>("input")!;
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.send(input);
    });
    if (!this.touch) input.focus();
  }

  private updateConversationHeader(): void {
    const conv = this.current;
    const el = this.paneEl.querySelector<HTMLDivElement>("[data-title]");
    if (!conv || !el) return;
    const title = this.titleOf(conv);
    const other = conv.kind === "direct" ? conv.members.find((m) => m.id !== this.me) : undefined;
    const sub = conv.kind === "group" ? `${conv.members.length} pessoas` : other ? "ver dossiê" : "conversa privada";
    el.innerHTML = other
      ? `<button type="button" class="dm-title-btn" data-dossier="${escapeHtml(other.id)}" title="Ver o dossiê de ${escapeHtml(title)}"><strong>${escapeHtml(title)}</strong><span>${escapeHtml(sub)}</span></button>`
      : `<strong>${escapeHtml(title)}</strong><span>${escapeHtml(sub)}</span>`;
    const ava = this.paneEl.querySelector<HTMLSpanElement>("[data-head-ava]");
    if (ava) ava.innerHTML = this.convAvatar(conv);
    const input = this.paneEl.querySelector<HTMLInputElement>(".dm-form input");
    if (input) input.placeholder = `Mensagem para ${title}…`;
  }

  /** Redesenha o log inteiro (abrir, carregar anteriores). */
  private renderLog(toBottom: boolean): void {
    const log = this.log;
    if (!log) return;
    const prevHeight = log.scrollHeight;
    const prevTop = log.scrollTop;
    log.replaceChildren();
    if (this.more) {
      const li = document.createElement("li");
      li.className = "dm-more";
      li.innerHTML = `<button type="button" class="link-btn" data-act="older">Carregar anteriores</button>`;
      log.append(li);
    } else if (!this.messages.length) {
      const li = document.createElement("li");
      li.className = "dm-empty";
      li.textContent = "Nenhuma mensagem ainda. Diga oi!";
      log.append(li);
    }
    let prev: DMMessage | undefined;
    for (const m of this.messages) {
      log.append(this.messageEl(m, prev));
      prev = m;
    }
    // carregar anteriores mantem a mensagem que estava na tela no lugar
    log.scrollTop = toBottom ? log.scrollHeight : prevTop + (log.scrollHeight - prevHeight);
  }

  private appendMessage(m: DMMessage): void {
    const log = this.log;
    if (!log || this.messages.some((x) => x.id === m.id)) return;
    const atBottom = log.scrollTop + log.clientHeight >= log.scrollHeight - 24;
    const prev = this.messages.at(-1);
    this.messages.push(m);
    log.querySelector(".dm-empty")?.remove();
    log.append(this.messageEl(m, prev));
    if (atBottom || m.senderId === this.me) log.scrollTop = log.scrollHeight;
  }

  private messageEl(m: DMMessage, prev: DMMessage | undefined): HTMLLIElement {
    const li = document.createElement("li");
    if (!prev || dayKey(prev.at) !== dayKey(m.at)) {
      const day = document.createElement("span");
      day.className = "dm-day";
      day.textContent = dayLabel(m.at);
      li.append(day);
    }
    if (m.kind === "system") {
      li.className = "dm-system";
      li.append(document.createTextNode(m.body));
      return li;
    }
    const mine = m.senderId === this.me;
    li.className = mine ? "dm-msg mine" : "dm-msg";
    // nome so no grupo e so quando muda quem fala
    const showName = !mine && this.current?.kind === "group" && (prev?.senderId !== m.senderId || prev?.kind === "system");
    if (showName) {
      const who = document.createElement(m.senderId ? "button" : "span");
      who.className = "dm-who";
      who.textContent = m.senderName || "Conta excluída";
      if (who instanceof HTMLButtonElement && m.senderId) {
        who.type = "button";
        who.dataset.dossier = m.senderId;
        who.title = `Ver o dossiê de ${m.senderName}`;
      }
      li.append(who);
    }
    const bubble = document.createElement("span");
    bubble.className = "dm-bubble";
    bubble.textContent = m.body;
    const time = document.createElement("time");
    time.textContent = clock(m.at);
    time.dateTime = m.at;
    bubble.append(time);
    li.append(bubble);
    return li;
  }

  private renderMembers(): void {
    const conv = this.current;
    if (!conv) return this.setPane(null);
    const rows = conv.members
      .map(
        (u) => `<li class="dm-person">${userAvatar(u)}<span><button type="button" class="dm-name-btn" data-dossier="${escapeHtml(u.id)}" title="Ver dossiê">${escapeHtml(u.name)}</button></span>${
          u.id === this.me
            ? `<em>você</em>`
            : `<button type="button" class="secondary small" data-direct="${escapeHtml(u.id)}">Conversar</button>`
        }</li>`,
      )
      .join("");
    this.paneEl.innerHTML = `
      <header class="dm-head">
        <button type="button" class="dm-icon" data-act="to-conversation" title="Voltar" aria-label="Voltar">‹</button>
        <div class="dm-title"><strong>${escapeHtml(this.titleOf(conv))}</strong><span>${conv.members.length} de ${DM_GROUP_MAX} pessoas</span></div>
        <button type="button" class="dm-icon" data-act="close-pane" title="Fechar (Esc)" aria-label="Fechar">✕</button>
      </header>
      <p class="notice error hidden" data-notice></p>
      <div class="dm-new">
        <button type="button" class="secondary small" data-act="add" ${conv.members.length >= DM_GROUP_MAX ? "disabled" : ""}>Adicionar pessoas</button>
        <button type="button" class="secondary small" data-act="rename">Renomear</button>
      </div>
      <ul class="dm-list dm-people">${rows}</ul>
      <button type="button" class="secondary small danger dm-leave" data-act="leave">Sair do grupo</button>
    `;
  }

  private renderPick(mode: PickMode): void {
    const multi = mode !== "direct";
    const adding = mode === "add";
    this.paneEl.innerHTML = `
      <header class="dm-head">
        ${adding ? `<button type="button" class="dm-icon" data-act="to-conversation" title="Voltar" aria-label="Voltar">‹</button>` : ""}
        <div class="dm-title"><strong>${adding ? "Adicionar ao grupo" : "Nova conversa"}</strong><span>${multi ? "escolha as pessoas" : "com quem?"}</span></div>
        <button type="button" class="dm-icon" data-act="close-pane" title="Fechar (Esc)" aria-label="Fechar">✕</button>
      </header>
      ${
        adding
          ? ""
          : `<div class="dm-tabs" role="tablist">
              <button type="button" role="tab" aria-selected="${mode === "direct"}" data-act="mode-direct">Individual</button>
              <button type="button" role="tab" aria-selected="${mode === "group"}" data-act="mode-group">Grupo</button>
            </div>`
      }
      <p class="notice error hidden" data-notice></p>
      ${
        mode === "group"
          ? `<div class="field dm-field"><input data-name type="text" maxlength="${DM_GROUP_NAME_MAX}" placeholder="Nome do grupo" autocomplete="off" /></div>`
          : ""
      }
      ${multi ? `<div class="dm-chips" data-chips></div>` : ""}
      <div class="field dm-field"><input data-search type="search" placeholder="Buscar pelo nome…" autocomplete="off" /></div>
      <ul class="dm-list dm-people" data-results><li class="dm-empty">Carregando…</li></ul>
      ${multi ? `<button type="button" class="primary dm-confirm" data-act="confirm-pick" disabled>${mode === "group" ? "Criar grupo" : "Adicionar"}</button>` : ""}
    `;
    const search = this.paneEl.querySelector<HTMLInputElement>("[data-search]")!;
    search.addEventListener("input", () => {
      if (this.searchTimer !== null) window.clearTimeout(this.searchTimer);
      this.searchTimer = window.setTimeout(() => void this.search(search.value), SEARCH_DELAY);
    });
    this.paneEl.querySelector<HTMLInputElement>("[data-name]")?.addEventListener("input", () => this.renderChips());
    this.renderChips();
    void this.search("");
    if (!this.touch) (this.paneEl.querySelector<HTMLInputElement>("[data-name]") ?? search).focus();
  }

  private async search(query: string): Promise<void> {
    const results = this.paneEl.querySelector<HTMLUListElement>("[data-results]");
    if (!results || this.pane?.kind !== "pick") return;
    const mode = this.pane.mode;
    try {
      const { users } = await dmApi.searchUsers(this.token, query.trim());
      if (this.pane?.kind !== "pick" || !results.isConnected) return;
      const inGroup = new Set(mode === "add" ? (this.current?.members.map((m) => m.id) ?? []) : []);
      const shown = users.filter((u) => !inGroup.has(u.id));
      results.innerHTML =
        shown.map((u) => this.personRow(u, mode)).join("") ||
        `<li class="dm-empty">${query.trim() ? "Ninguém com esse nome" : "Ninguém por aqui ainda"}</li>`;
    } catch (error) {
      results.innerHTML = `<li class="dm-empty">${escapeHtml(errorText(error))}</li>`;
    }
  }

  private personRow(u: DMUser, mode: PickMode): string {
    const on = this.picked.has(u.id);
    const action =
      mode === "direct"
        ? `<button type="button" class="secondary small" data-direct="${escapeHtml(u.id)}">Conversar</button>`
        : `<button type="button" class="secondary small${on ? " on" : ""}" data-pick="${escapeHtml(u.id)}" data-name="${escapeHtml(u.name)}" aria-pressed="${on}">${on ? "Escolhido" : "Escolher"}</button>`;
    return `<li class="dm-person">${userAvatar(u)}<span>${escapeHtml(u.name)}</span>${action}</li>`;
  }

  private renderChips(): void {
    const chips = this.paneEl.querySelector<HTMLDivElement>("[data-chips]");
    const confirm = this.paneEl.querySelector<HTMLButtonElement>("[data-act='confirm-pick']");
    if (chips) {
      chips.innerHTML = [...this.picked]
        .map(([id, name]) => `<button type="button" class="dm-chip" data-unpick="${escapeHtml(id)}" title="Tirar">${escapeHtml(name)} ✕</button>`)
        .join("");
    }
    if (confirm) {
      const name = this.paneEl.querySelector<HTMLInputElement>("[data-name]");
      confirm.disabled = this.picked.size === 0 || (name !== null && !name.value.trim());
    }
  }

  // ---------------------------------------------------------- acoes

  private onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const el = target.closest<HTMLElement>("[data-act], [data-open], [data-direct], [data-pick], [data-unpick], [data-dossier]");
    if (!el || !this.root.contains(el)) return;
    // com o foco num botao, Espaco e Enter (interagir, chat) o clicariam de novo
    if (el instanceof HTMLButtonElement && !el.closest("form")) el.blur();

    if (el.dataset.dossier) {
      this.dossierHandler?.(el.dataset.dossier);
      return;
    }
    if (el.dataset.open) {
      const conv = this.conversations.find((c) => c.id === el.dataset.open);
      if (conv && !(this.pane?.kind === "conversation" && this.current?.id === conv.id)) void this.openConversation(conv);
      return;
    }
    if (el.dataset.direct) {
      void this.startDirect(el.dataset.direct);
      return;
    }
    if (el.dataset.pick) {
      this.togglePick(el, el.dataset.pick);
      return;
    }
    if (el.dataset.unpick) {
      this.picked.delete(el.dataset.unpick);
      const row = this.paneEl.querySelector<HTMLButtonElement>(`[data-pick="${CSS.escape(el.dataset.unpick)}"]`);
      if (row) setPickButton(row, false);
      this.renderChips();
      return;
    }

    switch (el.dataset.act) {
      case "toggle":
        // com um painel aberto, expandir fecha o painel e mostra a lista larga
        if (this.pane) {
          this.closePane();
          return this.setExpanded(true);
        }
        return this.setExpanded(!this.expanded);
      case "close-touch":
        return this.closeTouch();
      case "close-pane":
        return this.closePane();
      case "to-conversation":
        return this.setPane({ kind: "conversation" });
      case "new":
        this.current = null;
        return this.setPane({ kind: "pick", mode: "direct" });
      case "mode-direct":
      case "mode-group": {
        const mode = el.dataset.act === "mode-group" ? "group" : "direct";
        if (this.pane?.kind === "pick" && this.pane.mode !== mode) this.setPane({ kind: "pick", mode });
        return;
      }
      case "members":
        return this.setPane({ kind: "members" });
      case "add":
        return this.setPane({ kind: "pick", mode: "add" });
      case "rename":
        return void this.rename();
      case "leave":
        return void this.leave();
      case "emoji":
        return void this.toggleEmoji();
      case "older":
        if (this.current) void this.loadMessages(this.current, false);
        return;
      case "confirm-pick":
        return void this.confirmPick(el as HTMLButtonElement);
    }
  }

  private togglePick(button: HTMLElement, id: string): void {
    // quem ja esta no grupo (ao adicionar) ou so quem cria (grupo novo)
    const base = this.pane?.kind === "pick" && this.pane.mode === "add" ? (this.current?.members.length ?? 0) : 1;
    if (this.picked.has(id)) {
      this.picked.delete(id);
    } else if (base + this.picked.size >= DM_GROUP_MAX) {
      this.flash(`Um grupo tem no máximo ${DM_GROUP_MAX} pessoas.`);
      return;
    } else {
      this.picked.set(id, button.dataset.name ?? "");
    }
    setPickButton(button, this.picked.has(id));
    this.renderChips();
  }

  // ---------------------------------------------------------- emojis

  private async toggleEmoji(): Promise<void> {
    if (this.emojiOpen) return this.closeEmoji();
    const box = this.paneEl.querySelector<HTMLDivElement>("[data-emoji]");
    if (!box) return;
    this.emojiOpen = true;
    this.setEmojiButton(true);
    box.classList.remove("hidden");
    box.textContent = "Carregando emojis…";
    document.addEventListener("pointerdown", this.onOutsideEmoji, true);
    try {
      const picker = await loadEmojiPicker();
      // fechou (ou trocou de tela) enquanto carregava
      if (!this.emojiOpen || !box.isConnected) return;
      picker.addEventListener("emoji-click", this.onEmojiClick);
      this.picker = picker;
      box.replaceChildren(picker);
    } catch {
      if (box.isConnected) box.textContent = "Não foi possível carregar os emojis.";
    }
  }

  private closeEmoji(): void {
    if (!this.emojiOpen) return;
    this.emojiOpen = false;
    document.removeEventListener("pointerdown", this.onOutsideEmoji, true);
    this.picker?.removeEventListener("emoji-click", this.onEmojiClick as EventListener);
    this.picker?.remove();
    this.picker = null;
    this.setEmojiButton(false);
    const box = this.paneEl.querySelector<HTMLDivElement>("[data-emoji]");
    box?.classList.add("hidden");
    box?.replaceChildren();
  }

  private setEmojiButton(open: boolean): void {
    this.paneEl.querySelector("[data-act='emoji']")?.setAttribute("aria-expanded", String(open));
  }

  /** Clique fora do seletor (e fora do botao dele) fecha. */
  private readonly onOutsideEmoji = (event: PointerEvent) => {
    const target = event.target;
    if (target instanceof Element && target.closest("[data-emoji], [data-act='emoji']")) return;
    this.closeEmoji();
  };

  /** Poe o emoji onde esta o cursor do campo; o seletor fica aberto para mais. */
  private readonly onEmojiClick = (event: EmojiClickEvent) => {
    const emoji = event.detail.unicode;
    const input = this.paneEl.querySelector<HTMLInputElement>(".dm-form input");
    if (!emoji || !input) return;
    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? start;
    const next = input.value.slice(0, start) + emoji + input.value.slice(end);
    // o maxlength do campo conta unidades UTF-16, como aqui
    if (next.length > DM_MAX_CHARS) return;
    input.value = next;
    const caret = start + emoji.length;
    input.setSelectionRange(caret, caret);
    // no toque, focar o campo abriria o teclado por cima do seletor
    if (!this.touch) input.focus();
  };

  private async send(input: HTMLInputElement): Promise<void> {
    const conv = this.current;
    const text = input.value.trim();
    if (!conv || !text || input.disabled) return;
    input.disabled = true;
    try {
      const { message } = await dmApi.send(this.token, conv.id, text);
      input.value = "";
      this.closeEmoji();
      // o eco do WebSocket pode chegar antes ou depois: receive ignora repetidas
      this.receive(message);
    } catch (error) {
      this.flash(errorText(error));
    } finally {
      input.disabled = false;
      if (this.current?.id === conv.id && input.isConnected) input.focus();
    }
  }

  private async startDirect(userId: string): Promise<void> {
    try {
      const { conversation } = await dmApi.direct(this.token, userId);
      this.upsert(conversation);
      await this.openConversation(conversation);
    } catch (error) {
      this.flash(errorText(error));
    }
  }

  private async confirmPick(button: HTMLButtonElement): Promise<void> {
    if (this.pane?.kind !== "pick") return;
    const mode = this.pane.mode;
    const ids = [...this.picked.keys()];
    button.disabled = true;
    try {
      let conversation: DMConversation;
      if (mode === "group") {
        const name = this.paneEl.querySelector<HTMLInputElement>("[data-name]")?.value.trim() ?? "";
        ({ conversation } = await dmApi.createGroup(this.token, name, ids));
      } else {
        if (!this.current) return;
        ({ conversation } = await dmApi.addMembers(this.token, this.current.id, ids));
      }
      this.upsert(conversation);
      await this.openConversation(conversation);
    } catch (error) {
      this.flash(errorText(error));
      button.disabled = false;
    }
  }

  private async rename(): Promise<void> {
    const conv = this.current;
    if (!conv) return;
    const result = await confirmAction({
      title: "Renomear grupo",
      message: "Todo mundo do grupo vê o nome novo.",
      confirmLabel: "Salvar",
      fields: [{ name: "name", label: "Nome do grupo", type: "text", value: conv.name ?? "", maxLength: DM_GROUP_NAME_MAX, required: true }],
      run: async (v) => {
        const { conversation } = await dmApi.rename(this.token, conv.id, v.name);
        this.upsert(conversation);
        if (this.current?.id === conversation.id) this.current = conversation;
      },
    });
    if (result === null) return;
    this.renderColumn();
    if (this.pane?.kind === "members") this.renderMembers();
  }

  private async leave(): Promise<void> {
    const conv = this.current;
    if (!conv) return;
    const result = await confirmAction({
      title: `Sair de ${this.titleOf(conv)}`,
      message: "Você para de receber as mensagens do grupo. Para voltar, alguém precisa te adicionar de novo.",
      confirmLabel: "Sair do grupo",
      danger: true,
      run: () => dmApi.leave(this.token, conv.id),
    });
    if (result === null) return;
    this.conversations = this.conversations.filter((c) => c.id !== conv.id);
    this.closePane();
    this.emitUnread();
  }

  // ---------------------------------------------------------- utils

  private titleOf(c: DMConversation): string {
    if (c.kind === "group") return c.name || "Grupo";
    return c.members.find((m) => m.id !== this.me)?.name ?? "Conta excluída";
  }

  private previewOf(c: DMConversation, m: DMMessage): string {
    if (m.kind === "system") return m.body;
    if (m.senderId === this.me) return `Você: ${m.body}`;
    return c.kind === "group" ? `${m.senderName || "Conta excluída"}: ${m.body}` : m.body;
  }

  /**
   * Miniatura da conversa: a cabeca de quem esta do outro lado, ou no grupo
   * um mosaico com ate 4 membros (sem contar quem ve). Sem ninguem, a inicial.
   */
  private convAvatar(c: DMConversation): string {
    const others = c.members.filter((m) => m.id !== this.me);
    if (c.kind === "direct") return others[0] ? userAvatar(others[0]) : initialAvatar("?", false);
    const shown = others.slice(0, 4);
    const heads = shown.map((u) => headURL(u));
    if (!shown.length || heads.some((h) => !h)) return initialAvatar(this.titleOf(c), true);
    if (shown.length === 1) return `<span class="dm-ava group"><img src="${escapeHtml(heads[0])}" alt="" /></span>`;
    return `<span class="dm-ava group mosaic n${shown.length}" aria-hidden="true">${heads
      .map((h) => `<img src="${escapeHtml(h)}" alt="" />`)
      .join("")}</span>`;
  }

  /** Erro no painel aberto, ate a proxima troca de tela. */
  private flash(text: string): void {
    const notice = this.paneEl.querySelector<HTMLParagraphElement>("[data-notice]");
    if (!notice) return;
    notice.textContent = text;
    notice.classList.remove("hidden");
  }

  /**
   * No toque a tela cheia ocupa a area visivel acima do teclado virtual (o
   * iOS ignora o interactive-widget e empurra a pagina), como o chat local.
   */
  private readonly fitViewport = () => {
    const viewport = window.visualViewport;
    if (!this.touchOpen || !viewport) return;
    this.root.style.setProperty("--dm-top", `${viewport.offsetTop}px`);
    this.root.style.setProperty("--dm-height", `${viewport.height}px`);
    if (this.log && this.pane?.kind === "conversation") this.log.scrollTop = this.log.scrollHeight;
  };
}

function setPickButton(button: HTMLElement, on: boolean): void {
  button.classList.toggle("on", on);
  button.setAttribute("aria-pressed", String(on));
  button.textContent = on ? "Escolhido" : "Escolher";
}

/** Cabeca do avatar da conta ("" enquanto as partes do avatar nao carregaram). */
function headURL(u: DMUser): string {
  if (!avatarPartsReady()) return "";
  try {
    return headDataURL(u.appearance ?? presetFor(u.skin));
  } catch {
    return "";
  }
}

function userAvatar(u: DMUser): string {
  const head = headURL(u);
  if (!head) return initialAvatar(u.name, false);
  return `<span class="dm-ava" aria-hidden="true"><img src="${escapeHtml(head)}" alt="" /></span>`;
}

function initialAvatar(name: string, group: boolean): string {
  const initial = [...name.trim()][0]?.toUpperCase() ?? "?";
  return `<span class="dm-ava initial${group ? " group" : ""}" aria-hidden="true">${escapeHtml(initial)}</span>`;
}

function loadExpanded(): boolean {
  try {
    return localStorage.getItem(EXPANDED_KEY) === "1";
  } catch {
    return false;
  }
}

function saveExpanded(expanded: boolean): void {
  try {
    localStorage.setItem(EXPANDED_KEY, expanded ? "1" : "0");
  } catch {
    // sem storage a barra so nao lembra da escolha
  }
}

function errorText(error: unknown): string {
  if (error instanceof ApiError) return Object.values(error.fields)[0] ?? error.message;
  return error instanceof Error ? error.message : "Algo deu errado.";
}

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function dayKey(iso: string): string {
  return new Date(iso).toDateString();
}

function dayLabel(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return "Hoje";
  if (date.toDateString() === yesterday.toDateString()) return "Ontem";
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** Hora se for de hoje, senao a data curta (lista de conversas). */
function shortTime(iso: string): string {
  const date = new Date(iso);
  if (date.toDateString() === new Date().toDateString()) return clock(iso);
  return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}
